// src/api/adminClinics.ts
// Admin Clinics API – uses shared authFetch and API_BASE from api/client.
// All requests go to the backend (Cloud Run / local FastAPI), never the frontend dev server.

import { V1, authFetch } from "api/client";
import type { SingleClinicFormValues } from "features/admin/components/SingleClinicForm";

export interface AdminClinic {
  id: number;
  name: string;
  slug: string;
  short_description: string;
  long_description?: string | null;
  city?: string | null;
  country?: string | null;       // ISO alpha-2
  postcode?: string | null;
  website_url?: string | null;
  phone?: string | null;
  email?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  created_date?: string | null;
  updated_date?: string | null;
  lock_version?: number;
  status?: "draft" | "published" | "archived";
}

export interface AdminClinicListOut {
  items: AdminClinic[];
  total?: number;
}

/**
 * List all clinics (admin only).
 * Supports both { items: [...] } and plain array responses.
 */
export async function adminListClinics(): Promise<AdminClinic[]> {
  const data = await authFetch<AdminClinicListOut | AdminClinic[]>(`${V1}/admin/clinics`, {
    method: "GET",
  });

  if (Array.isArray(data)) return data;
  if (Array.isArray((data as any)?.items)) return (data as any).items;
  return [];
}

/**
 * Create a single clinic from form values.
 */
export async function adminCreateClinic(values: SingleClinicFormValues): Promise<AdminClinic> {
  return authFetch<AdminClinic>(`${V1}/admin/clinics`, {
    method: "POST",
    body: JSON.stringify(values),
  });
}

/**
 * Upload a CSV of clinics for bulk import.
 * Backend handles validation and per-row geocoding.
 */
export interface UploadCSVResult {
  added?: number;
  updated?: number;
  failed?: number;
  errors?: string[];
}

export async function adminUploadClinicsCSV(file: File | Blob): Promise<UploadCSVResult> {
  const form = new FormData();
  form.append("file", file);

  return authFetch<UploadCSVResult>(`${V1}/admin/clinics/upload-csv`, {
    method: "POST",
    body: form,
  });
}

/**
 * Force backend to sync missing clinic coordinates.
 */
export interface SyncCoordsResult {
  updated: number;
  skipped?: number;
  errors?: string[];
}

export async function adminSyncClinicCoords(): Promise<SyncCoordsResult> {
  return authFetch<SyncCoordsResult>(`${V1}/admin/clinics/sync-coords`, {
    method: "POST",
  });
}

/**
 * Publish a clinic (draft → published or archived → published).
 */
export async function adminPublishClinic(
  clinicId: number,
  expectedLockVersion: number
): Promise<AdminClinic> {
  return authFetch<AdminClinic>(
    `${V1}/admin/clinics/${clinicId}/publish?expected_lock_version=${expectedLockVersion}`,
    { method: "POST" }
  );
}

/**
 * Archive a clinic (published → archived).
 */
export async function adminArchiveClinic(
  clinicId: number,
  expectedLockVersion: number
): Promise<AdminClinic> {
  return authFetch<AdminClinic>(
    `${V1}/admin/clinics/${clinicId}/archive?expected_lock_version=${expectedLockVersion}`,
    { method: "POST" }
  );
}

/**
 * Permanently delete a clinic.
 * Backend: DELETE /v1/admin/clinics/{clinic_id}
 * Returns 204 No Content on success.
 */
export async function adminDeleteClinic(clinicId: number): Promise<void> {
  // We don’t expect a JSON response (204)
  await authFetch<void>(`${V1}/admin/clinics/${clinicId}`, {
    method: "DELETE",
  });
}
